import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, DatePickerField, Text, TimeField, UseFormReturnType } from '@frontend/design-system';
import { getLabelWithTimezoneAbbr } from '../../helpers';
import { useWaitlistShallowStore } from '../../stores/waitlist.store';
import { apptDetailsFormFields, ApptDetailsFormFieldsName } from './use-waitlist-appointment-details-form';

interface Props {
  timezoneAbbr: string;
  form: UseFormReturnType<typeof apptDetailsFormFields, ApptDetailsFormFieldsName>;
  activeSlotCount: number;
  onSlotCountChange: (count: number) => void;
}

const MAX_SLOTS = 3;

const getSlotFieldKeys = (slotNumber: number) => {
  return {
    dateFieldKey: `date${slotNumber}` as ApptDetailsFormFieldsName,
    timeFieldKey: `time${slotNumber}` as ApptDetailsFormFieldsName,
  };
};

export const AppointmentSlots = ({ timezoneAbbr, form, activeSlotCount, onSlotCountChange }: Props) => {
  const { t } = useTranslation('schedule-quick-fill');

  const { selectedLocationId = '' } = useWaitlistShallowStore('selectedLocationId');

  const initialScrollTimeSlot1 = SchedulerV3.Helpers.useGetTimeFieldStartTime({
    date: form.values.date1,
    locationId: selectedLocationId,
  });

  const initialScrollTimeSlot2 = SchedulerV3.Helpers.useGetTimeFieldStartTime({
    date: form.values.date2,
    locationId: selectedLocationId,
  });

  const initialScrollTimeSlot3 = SchedulerV3.Helpers.useGetTimeFieldStartTime({
    date: form.values.date3,
    locationId: selectedLocationId,
  });

  const addSlot = () => {
    if (activeSlotCount >= MAX_SLOTS) return;

    onSlotCountChange(activeSlotCount + 1);
  };

  const removeSlot = (slotToRemove: number) => {
    if (activeSlotCount <= 1) return;

    // Shift slots up and clear the last slot
    const updates: Partial<Record<ApptDetailsFormFieldsName, any>> = {};
    for (let i = 1, nextSlot = 1; nextSlot <= activeSlotCount; i++) {
      if (i === slotToRemove) continue;
      const { dateFieldKey, timeFieldKey } = getSlotFieldKeys(nextSlot);
      const { dateFieldKey: srcDateKey, timeFieldKey: srcTimeKey } = getSlotFieldKeys(i);
      updates[dateFieldKey] = (form.values[srcDateKey] || '') as string;
      updates[timeFieldKey] = (form.values[srcTimeKey] || '') as string;
      nextSlot++;
    }

    form.seedValues(updates);
    onSlotCountChange(activeSlotCount - 1);
  };

  const isAddSlotDisabled = () => {
    // Check if current slots are filled before allowing new slot
    for (let i = 1; i <= activeSlotCount; i++) {
      const { dateFieldKey, timeFieldKey } = getSlotFieldKeys(i);
      if (!form.values[dateFieldKey] || !form.values[timeFieldKey]) {
        return true;
      }
    }
    return false;
  };

  const isDirectScheduling = form.values.directScheduling;
  const allowedSlots = isDirectScheduling ? 1 : MAX_SLOTS;
  return (
    <section>
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        {Array.from({ length: activeSlotCount }, (_, index) => {
          const slotNumber = index + 1;
          const { dateFieldKey, timeFieldKey } = getSlotFieldKeys(slotNumber);

          if (isDirectScheduling && slotNumber > 1) {
            return null;
          }

          return (
            <div css={slotCardStyle} key={slotNumber}>
              <div css={{ height: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text size='medium' weight='bold'>
                  {`${t('Appointment Slot')} ${isDirectScheduling ? '' : ` #${slotNumber}`}`}
                </Text>
                {!isDirectScheduling && activeSlotCount > 1 && (
                  <Button
                    variant='tertiary'
                    iconName='remove2'
                    size='small'
                    destructive
                    onClick={() => removeSlot(slotNumber)}
                  >
                    {t('Remove')}
                  </Button>
                )}
              </div>
              <div css={{ display: 'flex', gap: theme.spacing(2), padding: theme.spacing(1, 0) }}>
                <DatePickerField
                  {...form.getFieldProps(dateFieldKey as any)}
                  label={getLabelWithTimezoneAbbr(t('Date'), timezoneAbbr)}
                  css={{ paddingLeft: theme.spacing(1), flex: 1 }}
                />
                <TimeField
                  {...form.getFieldProps(timeFieldKey as any)}
                  showAdornment
                  label={getLabelWithTimezoneAbbr(t('Time'), timezoneAbbr)}
                  css={{ flex: 1 }}
                  initialScrollTime={
                    slotNumber === 1
                      ? initialScrollTimeSlot1
                      : slotNumber === 2
                      ? initialScrollTimeSlot2
                      : initialScrollTimeSlot3
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
      {activeSlotCount < allowedSlots && (
        <Button
          css={{ margin: theme.spacing(0.5, 0) }}
          variant='tertiary'
          iconName='add'
          size='small'
          onClick={addSlot}
          disabled={isAddSlotDisabled()}
        >
          {t('Add Slot')}
        </Button>
      )}
    </section>
  );
};

const slotCardStyle = css({
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(2),
});
